// src/sectors/pilates/members/modal/detail/tabs/GeneralTab.jsx
import React from 'react';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';

import { SoftCard } from 'shared/ui/soft/SoftCard';
import { RowItem } from 'shared/ui/soft/RowItem';
import { TabPanel } from 'shared/ui/soft/TabPanel';

function safeText(v, fallback = '—') {
  const s = (v ?? '').toString().trim();
  return s.length ? s : fallback;
}

function fmtTry(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('tr-TR')} TL`;
}

function fmtDateTR(iso) {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR');
}

function normalizePhoneForTel(input) {
  const s = (input ?? '').toString().trim();
  if (!s) return '';
  const cleaned = s.replace(/[^\d+]/g, '');
  return cleaned.startsWith('00') ? `+${cleaned.slice(2)}` : cleaned;
}

function canDial(phone) {
  const p = normalizePhoneForTel(phone);
  const digits = p.replace(/[^\d]/g, '');
  return digits.length >= 8;
}

function daysLeft(isoEnd) {
  if (!isoEnd) return null;
  const end = new Date(`${isoEnd}T00:00:00`);
  if (Number.isNaN(end.getTime())) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.ceil((end - startOfToday) / (1000 * 60 * 60 * 24));
}

export function GeneralTab({
  tab,
  member,
  activePackage,

  // opsiyonel override’lar
  name,
  phone,
  planName,
  statusLabelText,

  onPhoneClick,
  onPlanClick,
}) {
  const fullName = safeText(name ?? member?.fullName ?? member?.name);
  const rawPhone = phone ?? member?.phone;
  const phoneText = safeText(rawPhone);
  const planText = safeText(planName ?? member?.activePlanName ?? member?.planName, 'Paket yok');
  const statusText = safeText(
    statusLabelText ?? member?.membershipStatus ?? (member?.active ? 'ACTIVE' : 'PASSIVE')
  );

  // ✅ Üye bazlı toplam borç (paket borcu değil)
  const totalDebt = member?.debtTry;

  const ap = activePackage || null;

  const startText = fmtDateTR(ap?.startAt);
  const estEndText = fmtDateTR(ap?.estimatedEndAt);
  const endText = fmtDateTR(ap?.endAt);

  const left = daysLeft(ap?.estimatedEndAt);
  const leftText =
    left == null ? '—' : left < 0 ? `${Math.abs(left)} gün geçti` : `${left} gün kaldı`;

  const item0 = ap?.items?.[0];
  const usageText = item0 ? `${item0.usedSessions}/${item0.totalSessions} seans` : '—';

  const planPriceText = ap ? fmtTry(ap.planPriceTry) : '—';
  const agreedPriceText = ap ? fmtTry(ap.agreedPriceTry) : '—';

  const dial = normalizePhoneForTel(rawPhone);
  const dialEnabled = canDial(rawPhone);

  const phoneClickable = dialEnabled;
  const planClickable = !!onPlanClick;

  return (
    <TabPanel value={tab} index={0}>
      <MDBox sx={{ display: 'grid', gap: 1.25, mt: 1.5 }}>
        <SoftCard>
          {/* Üye üst satırı */}

          {/* ✅ Kartlar grid: mobil 1 kolon, sm+ 2 kolon */}
          <MDBox
            sx={{
              mt: 1,
              display: 'grid',
              gap: 1,
              // ✅ sm yerine md: dialog içinde bazen sm altına düşüyor
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },

              // ✅ Çocukların full-span yapmasını engelle
              '& > *': { minWidth: 0, gridColumn: 'auto !important' },
            }}
          >
            {/* Telefon */}
            <MDBox sx={{ minWidth: 0, cursor: phoneClickable ? 'pointer' : 'default' }}>
              <RowItem
                icon="call"
                tone="amber"
                title="Telefon"
                subtitle={phoneText}
                right={<Icon sx={{ opacity: phoneClickable ? 0.55 : 0.25 }}>phone</Icon>}
                onClick={
                  phoneClickable
                    ? () => {
                        onPhoneClick?.(member);
                        window.open(`tel:${dial}`, '_self');
                      }
                    : undefined
                }
              />
            </MDBox>

            {/* Aktif Paket */}
            <MDBox sx={{ minWidth: 0, cursor: planClickable ? 'pointer' : 'default' }}>
              <RowItem
                icon="inventory_2"
                tone="violet"
                title="Aktif Paket"
                subtitle={planText}
                right={<Icon sx={{ opacity: 0.35 }}>chevron_right</Icon>}
                onClick={planClickable ? () => onPlanClick(member) : undefined}
              />
            </MDBox>

            {/* Paket Fiyatı */}
            <MDBox sx={{ minWidth: 0 }}>
              <RowItem icon="sell" tone="blue" title="Paket Fiyatı" subtitle={planPriceText} />
            </MDBox>

            {/* Anlaşılan Fiyat */}
            <MDBox sx={{ minWidth: 0 }}>
              <RowItem
                icon="handshake"
                tone="teal"
                title="Anlaşılan Fiyat"
                subtitle={agreedPriceText}
              />
            </MDBox>

            {/* Paket Süresi */}
            <MDBox sx={{ minWidth: 0 }}>
              <RowItem
                icon="event"
                tone="blue"
                title="Paket Süresi"
                subtitle={
                  ap ? `${startText} → ${ap?.endAt ? endText : estEndText} (${leftText})` : '—'
                }
              />
            </MDBox>

            {/* Kullanım */}
            <MDBox sx={{ minWidth: 0 }}>
              <RowItem icon="repeat" tone="teal" title="Kullanım" subtitle={ap ? usageText : '—'} />
            </MDBox>

            {/* Toplam Borç */}
            <MDBox sx={{ minWidth: 0 }}>
              <RowItem
                icon="account_balance_wallet"
                tone="rose"
                title="Toplam Borç"
                subtitle={fmtTry(totalDebt)}
              />
            </MDBox>
          </MDBox>
        </SoftCard>
      </MDBox>
    </TabPanel>
  );
}
